import Vue from 'vue';
import KanbanAPI from '../../api/kanban';

export const STATUS_FUNNEL = 'status';
export const STATUS_COLUMNS = ['open', 'pending', 'resolved'];
let lastBoardRequest = 0;

const SORTS = {
  latest: { key: 'last_activity_at', direction: -1 },
  oldest: { key: 'last_activity_at', direction: 1 },
  newest: { key: 'created_at', direction: -1 },
  waiting: { key: 'waiting_since', direction: 1 },
};

const sortValue = (card, key) => {
  // No waiting_since (conversation already answered) goes last, like NULLS LAST in the database
  if (key === 'waiting_since' && !card.waiting_since) return Infinity;
  return card[key] || 0;
};

export const sortCards = (cards, sort) => {
  const { key, direction } = SORTS[sort] || SORTS.latest;
  return [...cards].sort(
    (a, b) => direction * (sortValue(a, key) - sortValue(b, key)) || b.id - a.id
  );
};

const isStatusFunnel = params => params.funnel_id === STATUS_FUNNEL;

// Column the conversation belongs to in the open board (null when it is not part of it)
const columnIdFor = (state, conversation) => {
  const columnId = isStatusFunnel(state.params)
    ? conversation.status
    : conversation.kanban_stage_id;
  return state.columns.some(column => column.id === columnId) ? columnId : null;
};

const matchesFilters = (params, conversation) => {
  const inboxIds = params.inbox_ids.map(Number);
  const teamIds = params.team_ids.map(Number);
  if (inboxIds.length && !inboxIds.includes(conversation.inbox_id)) {
    return false;
  }
  const teamId = conversation.meta?.team?.id;
  return !teamIds.length || teamIds.includes(teamId);
};

// Next pending schedule from a list of scheduled messages (timestamp in seconds or null)
export const nextPendingAt = scheduledMessages => {
  const pending = scheduledMessages
    .filter(item => item.status === 'pending')
    .map(item => item.scheduled_at);
  return pending.length ? Math.min(...pending) : null;
};

// Toast message when a move fails (board and widget)
export const moveErrorMessage = (error, t) => {
  const { status, data = {} } = error.response || {};
  if ([401, 403].includes(status)) return t('KANBAN.NOT_ALLOWED');
  return data.error || data.message || t('KANBAN.MOVE_ERROR');
};

const findCard = (state, conversationId) => {
  for (let index = 0; index < state.columns.length; index += 1) {
    const card = state.columns[index].conversations.find(
      conversation => conversation.id === conversationId
    );
    if (card) return { column: state.columns[index], card };
  }
  return {};
};

export const state = {
  funnels: [],
  params: {
    funnel_id: STATUS_FUNNEL,
    inbox_ids: [],
    team_ids: [],
    q: '',
    sort: 'latest',
  },
  columns: [],
  uiFlags: { isFetching: false, hasError: false },
  updatedAt: null,
};

export const getters = {
  getFunnels: $state => $state.funnels,
  getParams: $state => $state.params,
  getUIFlags: $state => $state.uiFlags,
  getUpdatedAt: $state => $state.updatedAt,
  getColumns: $state =>
    $state.columns.map(column => ({
      ...column,
      conversations: sortCards(column.conversations, $state.params.sort),
    })),
  getTotalCount: $state =>
    $state.columns.reduce((total, column) => total + column.count, 0),
  getStage: $state => stageId =>
    $state.funnels
      .flatMap(funnel => funnel.stages.map(stage => ({ ...stage, funnel })))
      .find(stage => stage.id === stageId),
};

export const actions = {
  async fetchFunnels({ commit }) {
    const { data } = await KanbanAPI.getFunnels();
    commit('SET_FUNNELS', data.payload);
  },

  async saveFunnel({ dispatch }, { id, ...funnel }) {
    const { data } = id
      ? await KanbanAPI.updateFunnel(id, funnel)
      : await KanbanAPI.createFunnel(funnel);
    await dispatch('fetchFunnels');
    return data;
  },

  async deleteFunnel({ dispatch }, id) {
    await KanbanAPI.deleteFunnel(id);
    await dispatch('fetchFunnels');
  },

  async fetchBoard({ commit, state: $state }, params) {
    // Filters changed in a row: only the response of the last fetch counts
    lastBoardRequest += 1;
    const request = lastBoardRequest;
    // Another funnel: the old columns (statuses x stages) cannot be read with the new params
    if (params.funnel_id !== $state.params.funnel_id) commit('SET_COLUMNS', []);
    commit('SET_PARAMS', params);
    commit('SET_UI_FLAG', { isFetching: true, hasError: false });
    try {
      const { data } = await KanbanAPI.getBoard(params);
      if (request === lastBoardRequest) commit('SET_COLUMNS', data.columns);
    } catch (error) {
      if (request === lastBoardRequest)
        commit('SET_UI_FLAG', { hasError: true });
    } finally {
      if (request === lastBoardRequest)
        commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchMore({ commit, state: $state }, columnId) {
    // The offset is what is already on screen: real time keeps the column equal to the server's
    const column = $state.columns.find(item => item.id === columnId);
    const request = lastBoardRequest;
    const { data } = await KanbanAPI.getBoard({
      ...$state.params,
      column: columnId,
      offset: column.conversations.length,
    });
    // The board was fetched again meanwhile (filters or funnel changed): this page no longer belongs to it
    if (request !== lastBoardRequest) return;
    commit('APPEND_CARDS', {
      columnId,
      conversations: data.columns[0].conversations,
    });
  },

  // Updates the screen before the server answers and reverts on error
  async moveConversation(
    { commit, state: $state },
    { conversationId, toColumnId }
  ) {
    const { column: fromColumn, card } = findCard($state, conversationId);
    if (!card || fromColumn.id === toColumnId) return;

    const attribute = isStatusFunnel($state.params)
      ? 'status'
      : 'kanban_stage_id';
    const expected = { [attribute]: card[attribute] };
    const target = { [attribute]: toColumnId };

    commit('MOVE_CARD', { card: { ...card, ...target }, toColumnId });
    try {
      await KanbanAPI.moveConversation(conversationId, target, expected);
    } catch (error) {
      commit('MOVE_CARD', { card, toColumnId: fromColumn.id });
      throw error;
    }
  },

  // Conversation events from ActionCable (created/updated/status_changed/assignee/read)
  applyConversationEvent({ commit, state: $state }, conversation) {
    if (!$state.columns.length || !conversation?.id) return;

    const { card } = findCard($state, conversation.id);
    const merged = { ...card, ...conversation };
    const visible = matchesFilters($state.params, merged);
    const toColumnId = visible ? columnIdFor($state, merged) : null;

    // With an active search there is no way to know whether a new conversation matches the term
    if (!card && (!toColumnId || $state.params.q)) return;
    commit('MOVE_CARD', { card: merged, toColumnId });
  },

  applyMessageEvent({ commit, dispatch, state: $state }, message) {
    const { card } = findCard($state, message.conversation_id);
    if (!card) return;

    // The schedule shown on the card is already due: it is probably this message, so fetches the next one
    if (card.next_scheduled_at && card.next_scheduled_at * 1000 <= Date.now()) {
      dispatch('refreshNextScheduled', card.id);
    }

    const changes = {
      last_activity_at: message.conversation?.last_activity_at,
      unread_count: message.conversation?.unread_count ?? card.unread_count,
    };
    // Same rule as the card: only messages exchanged with the contact (incoming, outgoing and template)
    if (!message.private && [0, 1, 3].includes(message.message_type)) {
      changes.messages = [message];
    }
    commit('UPDATE_CARD', { id: card.id, changes });
  },

  async refreshNextScheduled({ commit }, conversationId) {
    const { data } = await KanbanAPI.getScheduledMessages(conversationId);
    commit('UPDATE_CARD', {
      id: conversationId,
      changes: { next_scheduled_at: nextPendingAt(data.payload) },
    });
  },
};

export const mutations = {
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_FUNNELS($state, funnels) {
    $state.funnels = funnels;
  },
  SET_PARAMS($state, params) {
    $state.params = { ...$state.params, ...params };
  },
  SET_COLUMNS($state, columns) {
    $state.columns = columns;
    $state.updatedAt = Date.now();
  },
  APPEND_CARDS($state, { columnId, conversations }) {
    const column = $state.columns.find(item => item.id === columnId);
    const loadedIds = column.conversations.map(card => card.id);
    column.conversations.push(
      ...conversations.filter(card => !loadedIds.includes(card.id))
    );
  },
  // Removes the card from wherever it is and, when toColumnId is given, adds it to that column (adjusting the counts)
  MOVE_CARD($state, { card, toColumnId }) {
    $state.columns.forEach(column => {
      const index = column.conversations.findIndex(item => item.id === card.id);
      if (index === -1) return;
      column.conversations.splice(index, 1);
      column.count -= 1;
    });
    const toColumn = $state.columns.find(column => column.id === toColumnId);
    if (!toColumn) return;
    toColumn.conversations.unshift(card);
    toColumn.count += 1;
  },
  UPDATE_CARD($state, { id, changes }) {
    const { column } = findCard($state, id);
    if (!column) return;
    const index = column.conversations.findIndex(item => item.id === id);
    Vue.set(column.conversations, index, {
      ...column.conversations[index],
      ...changes,
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
